import {
  COUPON_CATEGORY_LABELS,
  COUPON_STATUS,
  COUPON_STATUS_LABELS,
  DISCOUNT_TYPES,
  ZERO,
} from "constants/coupons";
import { normalizeTenantIdList } from "helpers/couponScopeOptions";

import { normalizeDate } from "./dateUtils";

export const resolveCouponStatus = (
  effectiveDate,
  expiryDate,
  now = new Date(),
  apiStatus = ""
) => {
  const normalizedApiStatus = String(apiStatus ?? "").trim().toLowerCase();

  if (normalizedApiStatus === COUPON_STATUS.INACTIVE) {
    return COUPON_STATUS.INACTIVE;
  }

  const today = new Date(now);
  today.setHours(ZERO, ZERO, ZERO, ZERO);

  const effective = normalizeDate(effectiveDate);
  const expiry = normalizeDate(expiryDate);

  if (effective === null || expiry === null) {
    return COUPON_STATUS.UNSET;
  }

  if (effective !== null && today < effective) {
    return COUPON_STATUS.SCHEDULED;
  }

  if (expiry !== null && today > expiry) {
    return COUPON_STATUS.EXPIRED;
  }

  return COUPON_STATUS.ACTIVE;
};

export const getCouponStatusLabel = (status) =>
  COUPON_STATUS_LABELS[status] ?? status;

export const getCouponCategoryLabel = (category) => {
  const trimmed = category?.trim();
  if (!trimmed) return "—";
  return COUPON_CATEGORY_LABELS[trimmed] ?? trimmed;
};

export const mapCouponToFormValues = (coupon) => ({
  ...coupon,
  tenantIds: normalizeTenantIdList(coupon.tenantIds),
  tenantDetails: coupon.tenantDetails ?? [],
  currencySettings: coupon.currencySettings ?? {},
  remarks: coupon.remarks ?? "",
  totalCouponCount: coupon.totalCouponCount ?? "",
  isApplicableMultipleTimes: Boolean(coupon.isApplicableMultipleTimes),
  isApplicableForSingleUser: Boolean(coupon.isApplicableForSingleUser),
  uamId: coupon.uamId ?? "",
  selectAllPackages: Boolean(coupon.selectAllPackages),
  packageIds: coupon.packageIds ?? [],
  selectAllPlatforms: Boolean(coupon.selectAllPlatforms),
  platformIds: coupon.platformIds ?? [],
  status: coupon.status ?? "",
});

export const formatCouponDiscount = (coupon) => {
  if (coupon.discountType === DISCOUNT_TYPES.PERCENTAGE) {
    return `${coupon.discountValue}%`;
  }

  const currencyCode = String(coupon.currencyCode ?? "").trim();
  if (currencyCode !== "") {
    return `${currencyCode} ${coupon.discountValue}`;
  }

  return String(coupon.discountValue ?? "");
};

export const formatCouponDate = (dateStr) => {
  if (!dateStr) return "-";

  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const formatCouponDateRange = (start, end) =>
  `${formatCouponDate(start)} – ${formatCouponDate(end)}`;

export const formatUsageLimit = (coupon) => {
  const used = coupon.usedCount ?? ZERO;
  const total = coupon.totalCouponCount ?? coupon.totalUsageLimit ?? ZERO;
  return `${used} / ${total}`;
};

export const formatCouponCategory = (category) =>
  getCouponCategoryLabel(category);
